import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { getErrorMessage } from "utils/errorHelpers";
import type { DowngradeToFreeRequestDto } from "client/api-v2";

interface DowngradeToFreeDialogProps {
  open: boolean;
  subscriptionName: string;
  currentLifecycleType: string;
  handleClose: () => void;
  submitDowngrade: (request: DowngradeToFreeRequestDto) => Promise<void>;
}

export function DowngradeToFreeDialog({
  open,
  subscriptionName,
  currentLifecycleType,
  handleClose,
  submitDowngrade,
}: DowngradeToFreeDialogProps) {
  const [reason, setReason] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (open) {
      setReason("");
      setError(undefined);
    }
  }, [open]);

  const isValid = useMemo(() => !!reason.trim() && reason.length <= 500, [reason]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(undefined);
    try {
      await submitDowngrade({ reason: reason.trim() });
      handleClose();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Transition to Free</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid size={12}>
            <Box sx={{ mb: 2, mt: 1 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Subscription:</strong> {subscriptionName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                <strong>Current Lifecycle:</strong> {currentLifecycleType}
              </Typography>
            </Box>
          </Grid>

          <Grid size={12}>
            <Alert severity="warning">
              This <strong>immediately</strong> moves the subscription to the <strong>Free</strong> plan — paid features
              are lost now and the license is regenerated. Zuora billing is cancelled at the end of the current term.
            </Alert>
          </Grid>

          {error && (
            <Grid size={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          <Grid size={12}>
            <TextField
              autoFocus
              required
              fullWidth
              multiline
              rows={4}
              label="Reason"
              placeholder="Why is this customer being transitioned to Free?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              error={!!error && !reason.trim()}
              helperText={
                reason.length > 500
                  ? `Reason is too long (${reason.length}/500 characters)`
                  : !reason.trim() && !!error
                  ? "Reason is required"
                  : ""
              }
              disabled={isSubmitting}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting || !isValid}>
          {isSubmitting ? "Transitioning..." : "Transition to Free"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
